import {
  Box,
  ComponentMultiStyleConfig,
  FormControl,
  FormErrorMessage,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import React, { useMemo } from "react";
import {
  CMFormInputBaseFileAttachableTextArea,
  CMFormInputBaseFileAttachableTextAreaProps,
} from "../../../control";
import { CMFormControlHeader } from "../CMFormControlHeader/CMFormControlHeader";
import { useDomainObjectValidation } from "../../../../hooks/use-domain-object-validation";
import { CMFormInputTextAreaProps } from "../CMFormInputTextArea/CMFormInputTextArea";

export type CMFormInputFileAttachableTextAreaProps =
  {} & CMFormInputBaseFileAttachableTextAreaProps & CMFormInputTextAreaProps;

/**
 * FormInputFileAttachableTextAreaコンポーネント
 *
 * コメントやエラーメッセージの設定が可能。
 * 入力文字数に応じて自動でサイズを変更する。
 * 添付ファイルの追加が可能。
 */
export const CMFormInputFileAttachableTextArea = ({
  minRows,
  maxRows,
  editMode = "editable",
  valueObjectMeta,
  size = "md",
  resize = "vertical",
  noHeader = false,
  label,
  placeholder,
  value = null,
  changedFrom = null,
  requiredLabelProps,
  changeBadgeProperties,
  commentButtonProps,
  onChange,
  onValidate,
  files,
  buttonUploadLabel,
  uploadingLabel,
  onAddFile,
  onDeleteFile,
  onDownloadFile,
  sx,
  ...rest
}: CMFormInputFileAttachableTextAreaProps) => {
  const { validate, validationState } = useDomainObjectValidation(
    value ?? "",
    valueObjectMeta,
    onValidate,
  );

  const variant = useMemo(() => {
    if (validationState.state === "error") {
      return "error";
    }
    return "default";
  }, [validationState.state]);

  const styles = useMultiStyleConfig(
    "CMFormInputFileAttachableTextAreaStyles",
    { variant: variant },
  );

  const containerStyles = useMemo(
    () => ({
      ...styles.containerBase,
      ...(editMode === "readOnly" ? styles.containerReadOnly : undefined),
      ...sx,
    }),
    [styles.containerBase, styles.containerReadOnly, editMode, sx],
  );

  return (
    <Box sx={{ ...containerStyles, ...sx }} {...rest}>
      <FormControl isInvalid={validationState.state === "error"}>
        {!noHeader && (
          <CMFormControlHeader<string>
            label={label}
            value={value}
            requiredLabelProps={requiredLabelProps}
            changeBadgeProperties={changeBadgeProperties}
            commentButtonProps={commentButtonProps}
            sx={styles.header}
          />
        )}
        <CMFormInputBaseFileAttachableTextArea
          editMode={editMode}
          files={files}
          variant={variant}
          buttonUploadLabel={buttonUploadLabel}
          uploadingLabel={uploadingLabel}
          size={size}
          resize={resize}
          placeholder={placeholder}
          value={value}
          minRows={minRows}
          maxRows={maxRows}
          onAddFile={onAddFile}
          onDeleteFile={onDeleteFile}
          onDownloadFile={onDownloadFile}
          onBlur={validate}
          onChange={onChange}
          changedFrom={changedFrom}
        />
        {validationState.state === "error" &&
          editMode !== "readOnly" &&
          validationState.errors.length > 0 && (
            <FormErrorMessage sx={styles.error}>
              {validationState.errors[0].errorMessage}
            </FormErrorMessage>
          )}
      </FormControl>
    </Box>
  );
};

export const CMFormInputFileAttachableTextAreaStyles: ComponentMultiStyleConfig =
  {
    parts: ["header", "error", "containerBase", "buttonLabel"],
    baseStyle: {
      header: {
        mb: "2px",
      },
      error: {
        mt: "4px",
      },
      containerBase: {
        display: "flex",
        flexDirection: "column",
      },
    },
  };
